import { useEffect, useState } from 'react';
import { setToken } from 'slice/authSlice';
import { useDispatch } from 'react-redux';
import { hasAuthParams, useAuth as useOIDCAuth } from 'react-oidc-context';
import { useAuth } from 'hook/useAuth';

/**
 * A custom React hook that integrates with the OIDC (OpenID Connect) authentication flow.
 * It manages sign-in redirects and token initialization using the react-oidc-context library.
 * This hook automatically attempts to sign in the user if they are not already authenticated.
 * Once authenticated, it stores the user's ID token in the application's state using Redux.
 *
 * @returns {object} An object containing the token initialization status (`tokenInitialized`)
 * and any authentication error (`oidcAuthError`).
 */
function useOIDC() {
  const authOIDC = useOIDCAuth();
  const { resetOIDCToken } = useAuth();
  const dispatch = useDispatch();
  const [tokenInitialized, setTokenInitialized] = useState(false);

  useEffect(() => {
    if (authOIDC.user?.id_token) {
      dispatch(setToken(authOIDC.user.id_token));
      setTokenInitialized(true);
    } else {
      dispatch(setToken(null));
      setTokenInitialized(false);
    }
  }, [authOIDC.user, dispatch]);

  useEffect(() => {
    const unsubscribe = authOIDC.events.addSilentRenewError((error) => {
      console.warn('OIDC silent renew failed, dropping the local token', error);
      resetOIDCToken();
    });

    return () => {
      unsubscribe(); // auto-clean
    };
  }, [authOIDC.events, resetOIDCToken]);

  useEffect(() => {
    const shouldRedirectToSignIn =
      !hasAuthParams() &&
      !authOIDC.isAuthenticated &&
      !authOIDC.activeNavigator &&
      !authOIDC.isLoading;

    if (shouldRedirectToSignIn) {
      // Carry the pre-login deep link through the provider round-trip as OIDC custom state: this
      // HashRouter portal keeps the whole route — and the externalId query — inside location.hash,
      // which the redirect back to the (static, registered) redirect_uri would otherwise drop.
      // onSigninCallback restores it after auth; an empty hash simply leaves nothing to restore.
      authOIDC.signinRedirect({ state: { returnHash: window.location.hash } });
    }

    if (authOIDC.isAuthenticated && !tokenInitialized) {
      dispatch(setToken(authOIDC.user.id_token));
      setTokenInitialized(true);
    }
  }, [authOIDC, tokenInitialized, dispatch]);

  return { tokenInitialized, oidcAuthError: authOIDC.error };
}

export default useOIDC;
